import { supabase } from "@/integrations/supabase/client";
import { captureExceptionSafe } from "@/lib/sentry";

/**
 * Tracks errors already reported by ErrorBoundary so the global
 * `window.error` / `unhandledrejection` handlers do not double-report
 * the same React render crash to Sentry / admin_alerts.
 */
const REPORTED = new WeakSet<object>();

export function markErrorReported(err: unknown) {
  if (err && typeof err === "object") {
    try { REPORTED.add(err as object); } catch { /* swallow */ }
  }
}

function alreadyReported(err: unknown): boolean {
  return !!(err && typeof err === "object" && REPORTED.has(err as object));
}

/**
 * Global window error handler — catches unhandled errors and
 * promise rejections, reports them to admin_alerts.
 */
export function initErrorReporting() {
  // Unhandled JS errors
  window.addEventListener("error", (event) => {
    if (alreadyReported(event.error)) return;
    reportError(
      `${event.message} (${event.filename}:${event.lineno}:${event.colno})`,
      event.error?.stack || ""
    );
    if (event.error) {
      markErrorReported(event.error);
      captureExceptionSafe(event.error, { source: "window.onerror" });
    }
  });

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    if (alreadyReported(event.reason)) return;
    const message = event.reason instanceof Error
      ? event.reason.message
      : String(event.reason);
    const stack = event.reason instanceof Error ? event.reason.stack || "" : "";
    reportError(`Unhandled Promise Rejection: ${message}`, stack);
    const errObj = event.reason instanceof Error ? event.reason : new Error(message);
    markErrorReported(event.reason ?? errObj);
    captureExceptionSafe(errObj, { source: "unhandledrejection" });
  });
}

async function reportError(message: string, stack: string) {
  try {
    await supabase.rpc("log_frontend_error", {
      p_error_message: message.slice(0, 500),
      p_stack_trace: stack.slice(0, 2000),
      p_url: window.location.href,
      p_user_agent: navigator.userAgent,
    });
  } catch {
    // Best-effort — never let monitoring crash the app
  }
}
